// src/api/segments.ts
import { apiJson, apiNoContent } from "./client";
import { getCached, setCache } from "@/utils/segmentCache";
import type {
  TableInfo,
  ColumnInfo,
  ColumnValueDistribution,
  SegmentDTO,
  SegmentEstimateResponse,
  SegmentFilterGroup,
  CreateSegmentPayload,
  UpdateSegmentPayload,
  OverlapResponse,
  SizeSnapshot,
  ChangelogEntry,
  SegmentInsightsResponse,
  SqlPreviewResponse,
} from "@/types/segments";

const SCHEMA_TTL = 30 * 60 * 1000; // 30 min — backend cache guarantees freshness
const VALUES_TTL = 8 * 60 * 60 * 1000; // 8 h — matches backend VALUES_TTL; bust via { refresh }
const VALUES_NEG_TTL = 60 * 1000; // 60 s — short negative entry so a failure doesn't poison the 8 h success cache

// ============================================
// Schema Discovery (cached)
// ============================================

type DatasetResult = { items: { dataset_id: string; project_id: string }[] };

export async function listDatasets(
  dataSourceId: string
): Promise<DatasetResult> {
  const key = `ds:${dataSourceId}`;
  const cached = getCached<DatasetResult>(key, SCHEMA_TTL);
  if (cached) return cached;
  const result = await apiJson<DatasetResult>(
    `/segments/datasets?data_source_id=${encodeURIComponent(dataSourceId)}`
  );
  setCache(key, result);
  return result;
}

type TableResult = { items: TableInfo[] };

export async function listTables(
  dataSourceId: string,
  datasetId: string
): Promise<TableResult> {
  const key = `tbl:${dataSourceId}:${datasetId}`;
  const cached = getCached<TableResult>(key, SCHEMA_TTL);
  if (cached) return cached;
  const params = new URLSearchParams();
  params.set("data_source_id", dataSourceId);
  params.set("dataset_id", datasetId);
  const result = await apiJson<TableResult>(`/segments/tables?${params.toString()}`);
  setCache(key, result);
  return result;
}

type ColumnResult = { items: ColumnInfo[] };

export async function listColumns(
  dataSourceId: string,
  tableFqn: string
): Promise<ColumnResult> {
  const key = `col:${dataSourceId}:${tableFqn}`;
  const cached = getCached<ColumnResult>(key, SCHEMA_TTL);
  if (cached) return cached;
  const params = new URLSearchParams();
  params.set("data_source_id", dataSourceId);
  params.set("table_fqn", tableFqn);
  const result = await apiJson<ColumnResult>(`/segments/columns?${params.toString()}`);
  setCache(key, result);
  return result;
}

type ValuesResult = { items: ColumnValueDistribution[] };

/** In-flight request deduplication — if a prefetch is already fetching the same column, share the promise. */
const inflightValues = new Map<string, Promise<ValuesResult>>();

export async function getColumnValues(
  dataSourceId: string,
  tableFqn: string,
  column: string,
  limit = 50,
  opts?: { refresh?: boolean }
): Promise<ValuesResult> {
  const key = `val:${dataSourceId}:${tableFqn}:${column}:${limit}`;
  const negKey = `valneg:${dataSourceId}:${tableFqn}:${column}:${limit}`;
  const refresh = opts?.refresh === true;

  if (!refresh) {
    const cached = getCached<ValuesResult>(key, VALUES_TTL);
    if (cached) return cached;
    // Short negative entry — avoid re-hitting the BFF for a column we just
    // learned can't be served (mirrors the backend's brief negative cache).
    const neg = getCached<{ failed: true }>(negKey, VALUES_NEG_TTL);
    if (neg) throw new Error("Column values unavailable");
    const inflight = inflightValues.get(key);
    if (inflight) return inflight;
  }

  const params = new URLSearchParams();
  params.set("data_source_id", dataSourceId);
  params.set("table_fqn", tableFqn);
  params.set("column", column);
  params.set("limit", String(limit));
  if (refresh) params.set("refresh", "true");

  const promise = apiJson<ValuesResult>(`/segments/values?${params.toString()}`)
    .then((result) => {
      setCache(key, result);
      return result;
    })
    .catch((err) => {
      setCache(negKey, { failed: true });
      throw err;
    });

  if (refresh) return promise;

  const tracked = promise.finally(() => {
    inflightValues.delete(key);
  });
  inflightValues.set(key, tracked);
  return tracked;
}

// ============================================
// Column Values Prefetch Queue
// ============================================

/**
 * Prefetches column values in small batches so we don't flood the backend.
 * Pauses automatically when user-initiated fetches happen (via `getColumnValues`)
 * and resumes after they complete — giving user requests priority.
 */
class ValuesPrefetchQueue {
  private queue: { dataSourceId: string; tableFqn: string; column: string }[] = [];
  private running = false;
  private aborted = false;
  private concurrency = 3;
  private userFetches = 0;

  /** Signal that a user-initiated fetch is starting. Pauses prefetch. */
  userFetchStart() { this.userFetches++; }

  /** Signal that a user-initiated fetch completed. Resumes prefetch if idle. */
  userFetchEnd() {
    this.userFetches = Math.max(0, this.userFetches - 1);
  }

  /** Cancel current prefetch run (e.g. when table changes). */
  cancel() {
    this.aborted = true;
    this.queue = [];
  }

  /** Start prefetching values for a list of columns. Cancels any prior run. */
  start(dataSourceId: string, tableFqn: string, columns: string[]) {
    this.cancel();
    this.aborted = false;
    this.queue = columns.map((column) => ({ dataSourceId, tableFqn, column }));
    if (!this.running) this.drain();
  }

  private async drain() {
    this.running = true;
    while (this.queue.length > 0 && !this.aborted) {
      // Wait while user-initiated fetches are in progress
      while (this.userFetches > 0 && !this.aborted) {
        await new Promise((r) => setTimeout(r, 50));
      }
      if (this.aborted) break;

      const batch = this.queue.splice(0, this.concurrency);
      await Promise.allSettled(
        batch.map((item) =>
          getColumnValues(item.dataSourceId, item.tableFqn, item.column, 50)
        )
      );
    }
    this.running = false;
  }
}

export const valuesPrefetchQueue = new ValuesPrefetchQueue();

// ============================================
// Cache Refresh
// ============================================

export async function refreshSegmentCache(
  dataSourceId: string
): Promise<{ invalidated: number }> {
  return apiJson(
    `/segments/cache/refresh?data_source_id=${encodeURIComponent(dataSourceId)}`,
    { method: "POST" }
  );
}

// ============================================
// Estimate
// ============================================

export async function estimateSegment(payload: {
  data_source_id: string;
  table_fqn: string;
  definition: SegmentFilterGroup;
  count_column?: string;
  workspace_id?: string;
  force?: boolean;
  /** The saved segment being edited: the backend stores the result as its
   *  size when the definition is exactly the saved one. */
  segment_id?: string;
}): Promise<SegmentEstimateResponse> {
  return apiJson(`/segments/estimate`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ============================================
// SQL Preview
// ============================================

export async function sqlPreview(payload: {
  data_source_id: string;
  table_fqn: string;
  definition: SegmentFilterGroup;
  workspace_id?: string;
}): Promise<SqlPreviewResponse> {
  return apiJson(`/segments/sql-preview`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ============================================
// SQL Issue Reports
// ============================================

export async function reportSqlIssue(
  segmentId: string,
  payload: {
    sql_query: string;
    definition: SegmentFilterGroup;
    description: string;
  },
): Promise<{ public_id: string }> {
  return apiJson(`/segments/${encodeURIComponent(segmentId)}/sql-issues`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ============================================
// Overlap Analysis
// ============================================

export async function calculateOverlap(
  segmentIds: string[]
): Promise<OverlapResponse> {
  return apiJson(`/segments/overlap`, {
    method: "POST",
    body: JSON.stringify({ segment_ids: segmentIds }),
  });
}

// ============================================
// Segment CRUD
// ============================================

export async function listSegments(params?: {
  workspace_id?: string;
  folder_id?: string; // public_id or "root" for unfiled
  folder_recursive?: boolean;
  tag_ids?: string[]; // AND-semantics
  data_source_id?: string;
  search?: string;
  sort_by?: "created_at" | "updated_at" | "name" | "size";
  sort_order?: "asc" | "desc";
  limit?: number;
  offset?: number;
}): Promise<{ items: SegmentDTO[]; total: number }> {
  const q = new URLSearchParams();
  if (params?.workspace_id) q.set("workspace_id", params.workspace_id);
  if (params?.folder_id) q.set("folder_id", params.folder_id);
  if (params?.folder_recursive) q.set("folder_recursive", "true");
  if (params?.data_source_id) q.set("data_source_id", params.data_source_id);
  if (params?.search) q.set("search", params.search);
  if (params?.sort_by) q.set("sort_by", params.sort_by);
  if (params?.sort_order) q.set("sort_order", params.sort_order);
  if (params?.tag_ids && params.tag_ids.length > 0) {
    for (const t of params.tag_ids) q.append("tag_ids", t);
  }
  if (params?.limit) q.set("limit", String(params.limit));
  if (params?.offset) q.set("offset", String(params.offset));
  const qs = q.toString();
  return apiJson(`/segments${qs ? `?${qs}` : ""}`);
}

export async function getSegment(id: string): Promise<SegmentDTO> {
  return apiJson(`/segments/${encodeURIComponent(id)}`);
}

export async function createSegment(
  payload: CreateSegmentPayload
): Promise<SegmentDTO> {
  return apiJson(`/segments`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// SegmentUpdateResponse — backend wraps the updated segment with the
// list of build_requests and (if and_sync=true) destination_runs that
// were enqueued as a side-effect. See plan
// docs/plans-pending/2026-05-23-segment-edit-and-run-now-auto-trigger-vsbr.md
// Step 4. Inline-typed here pending a regen of @verity/shared-types.
export type PendingVsbrRef = {
  vsbr_public_id: string;
  data_source_id: string; // `ds_*` public_id, never raw UUID
};

export type PendingDestinationRunRef = {
  destination_public_id: string;
  run_public_id: string;
  kind: string; // "KV" | "BREVO" | "BQ" | "GOOGLE_ADS"
};

export type SegmentUpdateResponse = {
  segment: SegmentDTO;
  pending_builds: PendingVsbrRef[];
  pending_destination_runs: PendingDestinationRunRef[];
};

export async function updateSegment(
  id: string,
  payload: UpdateSegmentPayload,
  options?: { andSync?: boolean }
): Promise<SegmentUpdateResponse> {
  const params = options?.andSync ? "?and_sync=true" : "";
  return apiJson(`/segments/${encodeURIComponent(id)}${params}`, {
    method: "PUT",
    body: JSON.stringify(payload),
  });
}

export async function deleteSegment(id: string): Promise<void> {
  return apiNoContent(`/segments/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

/** Pause a segment — exclude it from the destination-sync update. Members are
 * removed from connected destinations on the next rebuild. Returns the updated
 * segment (is_paused=true). */
export async function pauseSegment(id: string): Promise<SegmentDTO> {
  return apiJson(`/segments/${encodeURIComponent(id)}/pause`, { method: "POST" });
}

/** Resume a paused segment — re-include it in the destination-sync update. */
export async function resumeSegment(id: string): Promise<SegmentDTO> {
  return apiJson(`/segments/${encodeURIComponent(id)}/resume`, { method: "POST" });
}

/** Queue a fresh visitor_segments build per connected destination (retry a
 * failed build). Requires account admin. Returns the unchanged segment plus the
 * builds that were enqueued. */
export async function rebuildSegment(id: string): Promise<SegmentUpdateResponse> {
  return apiJson(`/segments/${encodeURIComponent(id)}/rebuild`, { method: "POST" });
}

// ============================================
// Segment References (for segment nesting)
// ============================================

export type ReferenceableSegment = {
  id: string;
  name: string;
  last_estimated_count: number | null;
};

export async function listReferenceableSegments(params: {
  workspace_id: string;
  table_fqn: string;
  exclude_id?: string;
}): Promise<ReferenceableSegment[]> {
  const qs = new URLSearchParams({
    workspace_id: params.workspace_id,
    table_fqn: params.table_fqn,
  });
  if (params.exclude_id) qs.set("exclude_id", params.exclude_id);
  const res: { items: ReferenceableSegment[] } = await apiJson(
    `/segments/referenceable?${qs.toString()}`
  );
  return res.items;
}

export async function getSegmentReferencedBy(
  id: string
): Promise<{ id: string; name: string }[]> {
  const res: { items: { id: string; name: string }[] } = await apiJson(
    `/segments/${encodeURIComponent(id)}/referenced-by`
  );
  return res.items;
}

// ============================================
// Segment-export service account
// ============================================
// Legacy segment_exports CRUD (list/create/update/delete/runs/verify) removed
// 2026-07-03 — plan 2026-07-02-retire-legacy-destination-paths. Unified
// destinations live in api/destinations.ts.

// Per-account runtime service account exposed for segment-export grants.
// (GET, idempotent — no IAM side effects.)
export type SegmentExportSAResponse = {
  sa_email: string;
  project_id: string;
};

export async function getSegmentExportSA(): Promise<SegmentExportSAResponse> {
  return apiJson("/accounts/me/segment-export-sa", { method: "GET" });
}

// ============================================
// Insights
// ============================================

const INSIGHTS_TTL = 30 * 60 * 1000; // 30 min — backend cache (definition hash) guarantees freshness

export async function getSegmentInsights(
  segmentId: string
): Promise<SegmentInsightsResponse> {
  const key = `insights:${segmentId}`;
  const cached = getCached<SegmentInsightsResponse>(key, INSIGHTS_TTL);
  if (cached) return cached;
  const result = await apiJson<SegmentInsightsResponse>(
    `/segments/${encodeURIComponent(segmentId)}/insights`,
    { method: "POST" },
  );
  setCache(key, result);
  return result;
}

// ============================================
// Changelog & Size History
// ============================================

export async function getSegmentSizeHistory(
  segmentId: string,
  days = 90
): Promise<{ items: SizeSnapshot[] }> {
  return apiJson(
    `/segments/${encodeURIComponent(segmentId)}/size-history?days=${days}`
  );
}

export async function getSegmentChangelog(
  segmentId: string,
  limit = 50
): Promise<{ items: ChangelogEntry[] }> {
  return apiJson(
    `/segments/${encodeURIComponent(segmentId)}/changelog?limit=${limit}`
  );
}

// ============================================
// Segment Folders
// ============================================

import type {
  SegmentFolderDTO,
  SegmentTagDTO,
  SegmentColorToken,
} from "../types/segments";

export type CreateFolderPayload = {
  name: string;
  parent_id?: string | null;
  description?: string;
  color?: SegmentColorToken;
};

export type UpdateFolderPayload = {
  name?: string;
  description?: string;
  color?: SegmentColorToken | null;
  sort_order?: number;
};

export async function listSegmentFolders(): Promise<{ items: SegmentFolderDTO[] }> {
  return apiJson(`/segment-folders`);
}

export async function createSegmentFolder(
  payload: CreateFolderPayload
): Promise<SegmentFolderDTO> {
  return apiJson(`/segment-folders`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateSegmentFolder(
  id: string,
  payload: UpdateFolderPayload
): Promise<SegmentFolderDTO> {
  return apiJson(`/segment-folders/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function moveSegmentFolder(
  id: string,
  parent_id: string | null
): Promise<SegmentFolderDTO> {
  return apiJson(`/segment-folders/${encodeURIComponent(id)}/move`, {
    method: "POST",
    body: JSON.stringify({ parent_id }),
  });
}

export async function reorderSegmentFolders(
  items: Array<{ id: string; sort_order: number }>
): Promise<{ updated: number }> {
  return apiJson(`/segment-folders/reorder`, {
    method: "POST",
    body: JSON.stringify({ items }),
  });
}

export async function deleteSegmentFolder(id: string): Promise<void> {
  return apiNoContent(`/segment-folders/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

// ============================================
// Segment Tags
// ============================================

export type CreateTagPayload = {
  name: string;
  color?: SegmentColorToken;
};

export type UpdateTagPayload = {
  name?: string;
  color?: SegmentColorToken | null;
};

export async function listSegmentTags(): Promise<{ items: SegmentTagDTO[] }> {
  return apiJson(`/segment-tags`);
}

export async function createSegmentTag(
  payload: CreateTagPayload
): Promise<SegmentTagDTO> {
  return apiJson(`/segment-tags`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateSegmentTag(
  id: string,
  payload: UpdateTagPayload
): Promise<SegmentTagDTO> {
  return apiJson(`/segment-tags/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteSegmentTag(id: string): Promise<void> {
  return apiNoContent(`/segment-tags/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function attachSegmentTags(
  segmentId: string,
  tag_ids: string[]
): Promise<{ attached: number; already_present: number }> {
  return apiJson(`/segments/${encodeURIComponent(segmentId)}/tags`, {
    method: "POST",
    body: JSON.stringify({ tag_ids }),
  });
}

export async function detachSegmentTag(
  segmentId: string,
  tagId: string
): Promise<void> {
  return apiNoContent(
    `/segments/${encodeURIComponent(segmentId)}/tags/${encodeURIComponent(tagId)}`,
    { method: "DELETE" }
  );
}
